import React, { useEffect } from 'react';

const ProxyPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Proxy Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Proxy Pattern - provide placeholder or surrogate for another object to control access. Learn with virtual proxy, protection proxy, and caching examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Proxy Pattern - provide placeholder or surrogate for another object to control access. Learn with virtual proxy, protection proxy, and caching examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'proxy pattern, design patterns, structural patterns, access control, lazy loading, caching, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'proxy pattern, design patterns, structural patterns, access control, lazy loading, caching, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Proxy Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Control access to another object
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Proxy pattern provides a placeholder or surrogate for another object to control 
            access to it. It acts as an intermediary that can add additional functionality such 
            as lazy loading, access control, caching, or logging.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to control access to an object, add functionality without changing the object, 
            or defer expensive operations until they're actually needed. Direct access might be 
            inefficient, insecure, or inappropriate.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: Image Loading Proxy</h2>
          <div style={styles.codeBlock}>
{`// Subject interface
interface Image {
    void display();
    String getInfo();
}

// Real subject - expensive to create
class HighResolutionImage implements Image {
    private String filename;
    private byte[] imageData;
    
    public HighResolutionImage(String filename) {
        this.filename = filename;
        loadImageFromDisk(); // Expensive operation
    }
    
    private void loadImageFromDisk() {
        System.out.println("Loading high-resolution image: " + filename);
        // Simulate expensive loading operation
        try {
            Thread.sleep(2000); // Simulate 2-second load time
        } catch (InterruptedException e) {
            Thread.currentThread().interrupt();
        }
        
        // Simulate loading large image data
        imageData = new byte[1024 * 1024]; // 1MB of image data
        System.out.println("Image loaded: " + filename);
    }
    
    @Override
    public void display() {
        System.out.println("Displaying high-resolution image: " + filename);
    }
    
    @Override
    public String getInfo() {
        return "High-res image: " + filename + " (Size: " + imageData.length + " bytes)";
    }
}

// Proxy - controls access to the real subject
class ImageProxy implements Image {
    private String filename;
    private HighResolutionImage realImage;
    private boolean isLoaded = false;
    
    public ImageProxy(String filename) {
        this.filename = filename;
    }
    
    @Override
    public void display() {
        if (!isLoaded) {
            System.out.println("Creating real image object...");
            realImage = new HighResolutionImage(filename);
            isLoaded = true;
        }
        realImage.display();
    }
    
    @Override
    public String getInfo() {
        if (!isLoaded) {
            return "Image proxy: " + filename + " (Not loaded yet)";
        }
        return realImage.getInfo();
    }
}

// Client code
class ImageGallery {
    private List<Image> images;
    
    public ImageGallery() {
        images = new ArrayList<>();
    }
    
    public void addImage(String filename) {
        // Use proxy instead of real image for lazy loading
        images.add(new ImageProxy(filename));
        System.out.println("Added image to gallery: " + filename);
    }
    
    public void displayImage(int index) {
        if (index >= 0 && index < images.size()) {
            System.out.println("\\n--- Displaying image " + index + " ---");
            images.get(index).display();
        }
    }
    
    public void showGalleryInfo() {
        System.out.println("\\n--- Gallery Info ---");
        for (int i = 0; i < images.size(); i++) {
            System.out.println(i + ": " + images.get(i).getInfo());
        }
    }
}

// Usage
public class ProxyPatternDemo {
    public static void main(String[] args) {
        ImageGallery gallery = new ImageGallery();
        
        // Adding images is fast - only proxies are created
        gallery.addImage("vacation.jpg");
        gallery.addImage("family.jpg");
        gallery.addImage("wedding.jpg");
        
        // Show info - no real images loaded yet
        gallery.showGalleryInfo();
        
        // Display specific image - now the real image is loaded
        gallery.displayImage(0);
        
        // Show info again - first image is now loaded
        gallery.showGalleryInfo();
        
        // Display another image
        gallery.displayImage(1);
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Types of Proxies</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Virtual Proxy:</strong> Controls access to expensive-to-create objects (lazy loading)</li>
            <li style={styles.listItem}><strong>Protection Proxy:</strong> Controls access based on permissions</li>
            <li style={styles.listItem}><strong>Remote Proxy:</strong> Represents objects in different address spaces</li>
            <li style={styles.listItem}><strong>Caching Proxy:</strong> Caches results of expensive operations</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Lazy Loading:</strong> Defer expensive operations until needed</li>
            <li style={styles.listItem}><strong>Access Control:</strong> Control who can access the real object</li>
            <li style={styles.listItem}><strong>Caching:</strong> Cache results to improve performance</li>
            <li style={styles.listItem}><strong>Additional Functionality:</strong> Add logging, monitoring without changing the real object</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need lazy initialization of expensive objects</li>
            <li style={styles.listItem}>When you want to control access to an object</li>
            <li style={styles.listItem}>When you need to add functionality without modifying the original object</li>
            <li style={styles.listItem}>When working with remote objects or services</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Proxy pattern is versatile for controlling object access and adding functionality. 
            It's particularly useful for performance optimization through lazy loading and caching, 
            and for implementing security and access control mechanisms.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ProxyPattern;
